/*
 * idle-image-queue.js -- fetches lazy images ahead of the shopper.
 *
 * Native lazy loading waits until an image is near the viewport. On a slow
 * connection that leaves a blank box when someone scrolls quickly. This queue
 * starts downloading the lazy images that are *about* to be needed as soon as
 * the page has loaded and gone quiet, in page order, a couple at a time, so
 * they are usually there before the shopper arrives. Native lazy loading
 * stays in place underneath as the safety net.
 *
 * Opt-in. Nothing is touched unless a container carries data-idle-images.
 *
 *   <div data-idle-images> ...cards... </div>
 *
 * What it handles: <img loading="lazy"> that already has a src. It leaves
 * alone:
 *   - a card strip's later pictures ([data-item-deferred]): they have no src
 *     on purpose and are promoted by the strip when someone touches it
 *   - the header, dialogs, the cart drawer and the slideshow (which has its own
 *     queue), or anything inside [data-no-idle-images]
 *   - images that will never be shown (display:none somewhere above them).
 *     Images inside a hidden tab panel are queued, after everything visible.
 *
 * Order: visible-tab images nearest the viewport first, then hidden tabs. A
 * section the shopper stays in for about a second jumps to the front, and
 * IdleImageQueue.bump(element) does the same on demand (tab taps).
 *
 * Skipped entirely when the browser reports Save-Data or a 2G connection.
 */
(function () {
  if (window.IdleImageQueue) return;

  var connection = navigator.connection;
  if (connection && (connection.saveData || /2g/.test(connection.effectiveType || ''))) {
    window.IdleImageQueue = { bump: function () {}, stats: function () { return { skipped: true }; } };
    return;
  }

  var ROOT = '[data-idle-images]';
  var SKIP = 'header, dialog, cart-drawer-component, header-menu, slideshow-component, [data-no-idle-images]';
  var CONCURRENCY = 2;
  var TIMEOUT = 10000; // a stuck request must not stall the queue
  var DWELL = 1000; // how long a shopper stays in a section before it jumps ahead

  var seen = new WeakSet();
  var boosted = new WeakSet();
  var queue = [];
  var active = 0;
  var started = false;
  var counts = { queued: 0, loaded: 0 };

  function hiddenTabPanel(img) {
    return img.closest('[data-tab-panel][hidden]');
  }

  /** True unless something between the image and its tab panel (or the page) is display:none. */
  function willBeShown(img) {
    for (var node = img.parentElement; node; node = node.parentElement) {
      if (node.matches && node.matches('[data-tab-panel][hidden]')) return true;
      if (getComputedStyle(node).display === 'none') return false;
    }
    return true;
  }

  function eligible(img) {
    if (seen.has(img)) return false;
    if (img.getAttribute('loading') !== 'lazy') return false;
    if (img.hasAttribute('data-item-deferred')) return false;
    if (!img.getAttribute('src') && !img.getAttribute('srcset')) return false;
    if (img.complete && img.naturalWidth > 0) return false;
    if (img.closest(SKIP)) return false;
    return willBeShown(img);
  }

  /** Lower is sooner. Boosted first, then visible tabs by distance below the viewport, then hidden tabs. */
  function score(img) {
    if (boosted.has(img)) return -1e9;
    if (hiddenTabPanel(img)) return 1e9;
    var top = img.getBoundingClientRect().top;
    return top >= 0 ? top : 1e5 + Math.abs(top); // below first, then what has been scrolled past
  }

  function collect() {
    var found = [];
    document.querySelectorAll(ROOT + ' img[loading="lazy"]').forEach(function (img) {
      if (eligible(img)) { seen.add(img); found.push(img); }
    });
    if (found.length) {
      counts.queued += found.length;
      queue = queue.concat(found);
      resort();
    }
  }

  function resort() {
    var scored = queue.map(function (img) { return { img: img, s: score(img) }; });
    scored.sort(function (a, b) { return a.s - b.s; });
    queue = scored.map(function (x) { return x.img; });
  }

  function load(img) {
    return new Promise(function (resolve) {
      var done = false;
      function finish() { if (!done) { done = true; resolve(); } }
      img.addEventListener('load', finish, { once: true });
      img.addEventListener('error', finish, { once: true });
      setTimeout(finish, TIMEOUT);
      img.fetchPriority = 'low'; // behind the hero and anything the shopper asked for
      img.loading = 'eager';
      if (img.complete) finish();
    });
  }

  function pump() {
    while (started && active < CONCURRENCY && queue.length && !document.hidden) {
      var img = queue.shift();
      if (img.getAttribute('loading') !== 'lazy') continue; // something else already started it
      active++;
      load(img).then(function () {
        active--;
        counts.loaded++;
        pump();
      });
    }
  }

  function bump(element) {
    if (!element) return;
    element.querySelectorAll('img').forEach(function (img) {
      boosted.add(img);
      if (!seen.has(img) && eligible(img)) { seen.add(img); queue.push(img); counts.queued++; }
    });
    resort();
    pump();
  }

  /** A section the shopper stays in for about a second jumps to the front. */
  function watchSections() {
    if (!('IntersectionObserver' in window)) return;
    var timers = new WeakMap();
    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        var section = entry.target;
        if (entry.isIntersecting) {
          timers.set(section, setTimeout(function () { bump(section); }, DWELL));
        } else {
          clearTimeout(timers.get(section));
        }
      });
    }, { threshold: 0.1 });
    document.querySelectorAll(ROOT).forEach(function (root) {
      observer.observe(root.closest('.shopify-section') || root);
    });
  }

  function start() {
    started = true;
    collect();
    watchSections();
    pump();

    // Sections re-render and lists grow: pick up images that appear later.
    var timer;
    new MutationObserver(function () {
      clearTimeout(timer);
      timer = setTimeout(function () { collect(); pump(); }, 400);
    }).observe(document.body, { childList: true, subtree: true });

    // After a scroll settles, put what is now nearest first.
    var scrollTimer;
    window.addEventListener('scroll', function () {
      clearTimeout(scrollTimer);
      scrollTimer = setTimeout(function () { resort(); pump(); }, 300);
    }, { passive: true });

    document.addEventListener('visibilitychange', pump);
  }

  function whenQuiet() {
    var go = function () { try { start(); } catch (e) { /* the queue is an extra: never break the page */ } };
    if ('requestIdleCallback' in window) requestIdleCallback(go, { timeout: 3000 });
    else setTimeout(go, 1500);
  }

  if (document.readyState === 'complete') whenQuiet();
  else window.addEventListener('load', whenQuiet, { once: true });

  window.IdleImageQueue = {
    bump: bump,
    stats: function () { return { started: started, queued: counts.queued, loaded: counts.loaded, waiting: queue.length, active: active }; },
  };
})();
